import { saveSignedUrl } from './idb-store'

type TabSyncMessage =
  | { type: 'AUTH_RESET' }
  | { type: 'CACHE_INVALIDATE'; queryKey: string[] }
  | { type: 'EVENTS_UPDATED'; fetchedAt: string }
  | { type: 'SIGNED_URL'; key: string; signedUrl: string; expiresAt: number }

const CHANNEL_NAME = 'nest-tab-sync'

let channel: BroadcastChannel | null = null

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME)
  }
  return channel
}

function post(msg: TabSyncMessage) {
  try {
    getChannel()?.postMessage(msg)
  } catch { /* BroadcastChannel may be unavailable */ }
}

export function broadcastAuthReset() {
  post({ type: 'AUTH_RESET' })
}

export function broadcastCacheInvalidation(queryKey: string[]) {
  post({ type: 'CACHE_INVALIDATE', queryKey })
}

export function broadcastEventsUpdated(fetchedAt: string) {
  post({ type: 'EVENTS_UPDATED', fetchedAt })
}

export function broadcastSignedUrl(key: string, signedUrl: string, expiresAt: number) {
  post({ type: 'SIGNED_URL', key, signedUrl, expiresAt })
}

/**
 * Subscribe to tab sync messages. Returns an unsubscribe function.
 */
export function onTabSync(handler: (msg: TabSyncMessage) => void): () => void {
  const ch = getChannel()
  if (!ch) return () => {}

  const listener = (event: MessageEvent<TabSyncMessage>) => {
    // Also write signed URLs from other tabs into our IndexedDB
    if (event.data.type === 'SIGNED_URL') {
      const { key, signedUrl, expiresAt } = event.data
      const remainingSec = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
      if (remainingSec > 60) {
        void saveSignedUrl(key, signedUrl, remainingSec)
      }
    }
    handler(event.data)
  }
  ch.addEventListener('message', listener)
  return () => ch.removeEventListener('message', listener)
}

export type { TabSyncMessage }
